import type {
    ClassifiedDomain,
    CreatedIndustry,
    CustomIndustryAgentState,
} from "../../CustomHooks/customIndustryAgent.types";
import { UNCAPPED_SEGMENT_BUDGET } from "../segmentBudget";

export type { ClassifiedDomain, CreatedIndustry, CustomIndustryAgentState };

export type PersistedCustomIndustryAgentRun = {
    threadId: string;
    runId?: string;
    countryId: number;
    description: string;
    // An error is shown once and never restored, so it is never stored either.
    status: Exclude<CustomIndustryAgentState, "idle" | "error">;
    completedSteps: number;
    discoveredDomains: ClassifiedDomain[] | null;
    topicSummary?: string | null;
    trafficFailures?: string[] | null;
    createdSegments?: ClassifiedDomain[] | null;
    rulesGenerated?: number;
    maxSegments?: number;
    segmentsOverBudget?: number;
    industryId?: string;
    industryName?: string;
    errorCode?: string | null;
    startedAt?: number;
};

export type CustomIndustryAgentRunSnapshot = {
    status: CustomIndustryAgentState;
    completedSteps: number;
    discoveredDomains: ClassifiedDomain[] | null;
    // Below: a clean, human-readable summary of what each backend node is doing, as it streams
    // in — surfaced by the checklist so "what's happening under the hood" isn't just a spinner.
    topicSummary: string | null;
    trafficFailures: string[] | null;
    createdSegments: ClassifiedDomain[] | null;
    // `create_segments` fans out into one `generate_rules` sub-event per candidate domain
    // before it reports its own final result — this counts those as they arrive, so the
    // checklist can show live progress instead of sitting silent for the longest step.
    rulesGenerated: number;
    // The segment budget this run was started with (negative means uncapped) and how many
    // domains needed a segment but did not fit it — together they are what tells the user a
    // larger budget would have produced a richer industry.
    maxSegments: number;
    segmentsOverBudget: number;
    // On an `error` run, set means the industry was created but this client could not open it.
    industry: CreatedIndustry | null;
    // Kept as the agent's raw code: it can send codes this client does not know yet, and those
    // still end the run as `rejected`.
    errorCode: string | null;
    description: string;
    countryId: number | null;
    threadId: string | null;
    runId: string | null;
    // When the run was started, persisted so the poll deadline survives a reload. Without it
    // a run that died before reporting a verdict would be polled afresh after every reload.
    startedAt: number | null;
};

export const IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT: CustomIndustryAgentRunSnapshot = {
    status: "idle",
    completedSteps: 0,
    discoveredDomains: null,
    topicSummary: null,
    trafficFailures: null,
    createdSegments: null,
    rulesGenerated: 0,
    maxSegments: UNCAPPED_SEGMENT_BUDGET,
    segmentsOverBudget: 0,
    industry: null,
    errorCode: null,
    description: "",
    countryId: null,
    threadId: null,
    runId: null,
    startedAt: null,
};
